import { AccessControlProviderType } from '../../../models/access-control/dto/access-control-config.dto';
import { AccessLogEventType } from '../../../models/access-control/dto/get-access-logs.dto';

export const PROVIDER_LABELS: Record<AccessControlProviderType, string> = {
  [AccessControlProviderType.ENZO_CARD]: 'Enzo Card',
  [AccessControlProviderType.NEOP]: 'Neop',
  [AccessControlProviderType.TENKRAFT]: 'Tenkraft',
  [AccessControlProviderType.IGLOOHOME]: 'Igloohome',
  [AccessControlProviderType.SALTO]: 'SALTO',
};

export function getProviderLabel(provider: AccessControlProviderType): string {
  return PROVIDER_LABELS[provider] ?? provider;
}

/** Chakra `colorScheme` per access-log event type — granted/denied semantics. */
export function getAccessLogEventColorScheme(eventType: AccessLogEventType): string {
  switch (eventType) {
    case AccessLogEventType.ENTRY:
    case AccessLogEventType.EXIT:
      return 'green';
    case AccessLogEventType.DENIED:
      return 'red';
    case AccessLogEventType.FORCED:
      return 'orange';
    default:
      return 'gray';
  }
}

export type CredentialFieldSpec = {
  key: string;
  required: boolean;
  /** Render as a password input — API keys/secrets. */
  secret?: boolean;
};

/**
 * Known credential keys per provider, mirroring what each API provider
 * actually reads from `config.credentials` (see
 * `api/src/access-control/provider/*.provider.ts`). An empty list means the
 * provider has no structured keys (Enzo Card is env-configured) — the
 * credentials modal falls back to a raw JSON editor for those.
 */
export const CREDENTIAL_FIELDS: Record<AccessControlProviderType, CredentialFieldSpec[]> = {
  [AccessControlProviderType.ENZO_CARD]: [],
  [AccessControlProviderType.NEOP]: [
    { key: 'apiKey', required: true, secret: true },
    { key: 'siteId', required: true },
    { key: 'baseUrl', required: false },
  ],
  [AccessControlProviderType.TENKRAFT]: [
    { key: 'apiKey', required: true, secret: true },
    { key: 'hubId', required: true },
    { key: 'baseUrl', required: false },
  ],
  [AccessControlProviderType.IGLOOHOME]: [
    { key: 'clientId', required: true },
    { key: 'clientSecret', required: true, secret: true },
    { key: 'bridgeId', required: false },
  ],
  [AccessControlProviderType.SALTO]: [
    { key: 'apiKey', required: true, secret: true },
    { key: 'siteId', required: true },
    { key: 'baseUrl', required: false },
  ],
};

export type DoorMappingRow = { courtId: string; doorId: string };

/** `{courtId: doorId}` -> editable rows. Stable order via Object.entries. */
export function doorMappingsToRows(doorMappings: Record<string, string>): DoorMappingRow[] {
  return Object.entries(doorMappings).map(([courtId, doorId]) => ({ courtId, doorId }));
}

/** Editable rows -> `{courtId: doorId}`. Rows with an empty courtId are dropped. */
export function rowsToDoorMappings(rows: DoorMappingRow[]): Record<string, string> {
  return rows.reduce<Record<string, string>>((acc, row) => {
    if (row.courtId.trim() !== '') acc[row.courtId] = row.doorId;
    return acc;
  }, {});
}
